import clsx from 'clsx'

import { COMPLETED_ACCENT, resolveResourceType } from '@/shared/resourceModuleParity'
import { GameIcon } from '@/shared/ui/GameIcon'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { ResourceModuleIcon } from '@/shared/ui/ResourceModuleIcon'

import type { Goal } from '../model/milestones'
import { getLadderState, milestoneTarget } from '../model/milestones'
import {
  ladderActionLabel,
  ladderActiveMilestoneLabel,
  ladderActiveMilestonePercent,
  ladderSummaryLabel,
  resourceNoun
} from '../model/milestoneCopy'

type Props = {
  goal: Extract<Goal, { kind: 'ladder' }>
}

/**
 * Corpo do sheet em modo escada.
 *
 * Lista vertical de todos os marcos, e não timeline horizontal: a vertical
 * escala para qualquer N, a horizontal não passa de meia dúzia a 375px.
 *
 * O detalhamento marco a marco vive aqui, e não no card — listar 5, 10, 15 …
 * 75 no card colapsado seria ruído, e a aritmética de largura nem permitiria.
 */
export const MilestoneDetailContent = ({ goal }: Props) => {
  const state = getLadderState(goal.milestones)
  const sample = goal.milestones[0] ?? null
  const activeMilestone = state.nextMilestone ?? sample
  const condition = sample?.objective_conditions?.[0]
  const resolved = resolveResourceType(condition?.resource_type)
  const accent = state.completed ? COMPLETED_ACCENT : resolved.accentColor

  return (
    <>
      <div
        className="mb-4 rounded-xl p-4"
        style={{
          background: `linear-gradient(135deg, ${accent}18 0%, ${accent}08 100%)`,
          border: `1px solid ${accent}25`
        }}
      >
        <div className="flex items-start gap-3">
          <ResourceModuleIcon
            resourceType={condition?.resource_type}
            size={28}
            tileSize={56}
            completed={state.completed}
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="m-0 text-caps font-bold uppercase tracking-[0.08em] text-gray-700">
              Meta
            </p>
            <h3 className="m-0 mt-0.5 text-base font-bold leading-snug text-gray-900">
              {goal.group.name}
            </h3>
          </div>
        </div>

        <div className="mt-3">
          <p className="m-0 mb-1.5 text-2xs text-gray-700">
            {ladderActionLabel(state, sample)}
          </p>
          <ProgressBar.Row
            value={state.completed ? 100 : ladderActiveMilestonePercent(state)}
            variant={state.completed ? 'success' : state.currentCount > 0 ? 'progress' : 'primary'}
            size="sm"
            label={ladderActiveMilestoneLabel(state, activeMilestone)}
          />
          <p className="m-0 mt-1.5 text-2xs font-semibold" style={{ color: accent }}>
            {ladderSummaryLabel(state, sample)}
          </p>
        </div>
      </div>

      {goal.group.description && (
        <p className="mb-4 text-sm leading-relaxed text-gray-700">
          {goal.group.description}
        </p>
      )}

      {state.nextMilestone && (
        <>
          <p className="mb-2 text-caps font-bold uppercase tracking-[0.08em] text-gray-700">
            Próximo marco
          </p>
          <div className="mb-4 rounded-lg border border-gray-100 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-gray-900">
                {state.nextTarget} {resourceNoun(sample, state.nextTarget ?? 0)}
              </span>
              <span className="flex items-center gap-1 text-xs font-bold text-gray-900">
                <GameIcon name="coins" size={13} />+{state.nextReward}
              </span>
            </div>
            <span className="mt-0.5 block text-2xs text-gray-700">
              faltam {state.remaining}
            </span>
          </div>
        </>
      )}

      <div className="mb-3 flex items-center gap-2">
        <span className="text-caps font-bold uppercase tracking-[0.08em] text-gray-700">
          Todos os marcos
        </span>
        <div className="flex-1 border-t border-gray-100" />
        <span className="text-2xs text-gray-700">
          {state.totalMilestones} marcos
        </span>
      </div>

      <ul className="m-0 flex list-none flex-col gap-2 p-0 pb-2">
        {goal.milestones.map((item, index) => {
          const target = milestoneTarget(item)
          const achieved = index < state.achievedCount
          const isNext = index === state.achievedCount

          return (
            <li
              key={item.id}
              className={clsx(
                'flex items-center gap-3 rounded-lg border px-3 py-2',
                achieved
                  ? 'border-green-200 bg-green-50'
                  : isNext
                    ? 'border-gray-200 bg-white'
                    : 'border-gray-100 bg-white'
              )}
            >
              <span
                aria-hidden
                className={clsx(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-2xs font-black',
                  achieved
                    ? 'bg-green-500 text-white'
                    : isNext
                      ? 'bg-gray-900 text-white'
                      : 'border border-gray-200 bg-white text-gray-400'
                )}
              >
                {achieved ? '✓' : index + 1}
              </span>

              <span
                className={clsx(
                  'min-w-0 flex-1 truncate text-xs font-semibold',
                  achieved ? 'text-green-800' : 'text-gray-900'
                )}
              >
                {target} {resourceNoun(sample, target)}
              </span>

              <span className="flex shrink-0 items-center gap-1 text-2xs font-bold text-gray-800">
                <GameIcon name="coins" size={11} />+{item.coin_reward ?? 0}
              </span>

              <span
                className={clsx(
                  'w-[92px] shrink-0 text-right text-2xs font-semibold',
                  achieved ? 'text-green-700' : isNext ? 'text-gray-900' : 'text-gray-500'
                )}
              >
                {achieved ? 'Conquistado' : isNext ? 'Próximo' : 'Pendente'}
              </span>
            </li>
          )
        })}
      </ul>
    </>
  )
}
